import { useEffect, useState } from "react";
import { InfoHint } from "../components/InfoHint";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { hasPlanFeature } from "../lib/planFeatures";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { StatusBadge, PrioritaBadge } from "../components/StatusBadge";
import { StarRating } from "../components/StarRating";
import {
  ClipboardList, CheckCircle2, AlertTriangle, Star, PlusCircle, Building2, Wrench, FolderOpen, ShieldAlert, MessageCircle, X, ChevronRight, ChevronDown, ChevronUp, FileText, Repeat, Scale, Gavel, FileEdit, Palette,
} from "lucide-react";

const OPEN_STATES = ["Nuova", "Affidata", "In lavorazione", "Eseguito"];

// Effetto "a rilievo" del testo su tile chiare
const EMB_LIGHT = "[text-shadow:0_1px_0_rgba(255,255,255,0.7)]";

// Gruppo comandi: fondo bordeaux, bordo nero in rilievo
const GROUP_CLS = "p-4 sm:p-5 bg-brand border border-black/40 mb-4 rounded-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_4px_0_-1px_rgba(0,0,0,0.28),0_14px_24px_-12px_rgba(0,0,0,0.45)]";

// Tile comando in rilievo (bordo nero tenue + ombra)
const TILE_RAISE = "shadow-[0_2px_0_-1px_rgba(0,0,0,0.22),0_5px_10px_-4px_rgba(0,0,0,0.25)]";

// Tile bianche/tono-su-tono che risaltano sul bordeaux, accento per tipologia
const TONES = {
  // Aperte/Aperti — bianca, accento bordeaux
  chiaro: { card: "bg-white border-black/25", icon: "bg-brand/12 text-brand", num: `text-[#2B0712] ${EMB_LIGHT}`, lbl: "text-brand-dark", ring: "ring-[#D4AF5A] border-[#D4AF5A]" },
  // Chiuse/Definiti — bianca, accento ORO
  scuro: { card: "bg-white border-black/25", icon: "bg-[#D4AF5A] text-[#3B0F20]", num: `text-[#2B0712] ${EMB_LIGHT}`, lbl: "text-brand-dark", ring: "ring-[#D4AF5A] border-[#D4AF5A]" },
  // In ritardo/Scaduti — tile ORO piena per richiamare l'attenzione
  alert: { card: "bg-[#D4AF5A] border-black/25", icon: "bg-brand text-white", num: `text-[#3B0F20] ${EMB_LIGHT}`, lbl: "text-brand-dark", ring: "ring-brand border-brand" },
  // Totali/Anagrafiche — bianca neutra, accento bordeaux tenue
  neutro: { card: "bg-white border-black/25", icon: "bg-brand/10 text-brand", num: `text-[#3B0F20] ${EMB_LIGHT}`, lbl: "text-brand-dark", ring: "ring-[#D4AF5A] border-[#D4AF5A]" },
};

function StatCard({ icon: Icon, label, value, tone, testid, onClick, active }) {
  const t = TONES[tone] || TONES["neutro"];
  return (
    <Card data-testid={testid} onClick={onClick}
      className={`p-4 border-2 transition-all duration-200 ${t.card} ${onClick ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-lg" : ""} ${active ? `ring-2 ${t.ring} shadow-lg` : "shadow-sm"}`}>
      <div className="flex items-center gap-3">
        <div className={`rounded-xl p-2.5 shrink-0 ${t.icon}`}><Icon className="w-6 h-6" /></div>
        <div className="min-w-0">
          <div className={`text-3xl font-display font-extrabold leading-none ${t.num}`}>{value}</div>
          <div className={`text-sm font-bold flex items-center gap-1 mt-1.5 ${t.lbl}`}>
            <span className="truncate">{label}</span>
            {onClick && <ChevronRight className={`w-3.5 h-3.5 shrink-0 transition-transform ${active ? "rotate-90" : ""}`} />}
          </div>
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [tickets, setTickets] = useState(null);
  const [sinistri, setSinistri] = useState(null);
  const [preventivi, setPreventivi] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const toggle = (key) => {
    const next = expanded === key ? null : key;
    setExpanded(next);
    if (!next) return;
    if (key.startsWith("sin")) {
      if (sinistri === null) api.get("/sinistri").then((r) => setSinistri(r.data)).catch(() => setSinistri([]));
    } else if (key.startsWith("prev")) {
      if (preventivi === null) api.get("/preventivi").then((r) => setPreventivi(r.data)).catch(() => setPreventivi([]));
    } else if (tickets === null) {
      api.get("/tickets").then((r) => setTickets(r.data)).catch(() => setTickets([]));
    }
  };

  if (!stats) return <div className="text-lg">Caricamento...</div>;

  const title = {
    admin: "Dashboard",
    condomino: "Le mie manutenzioni",
    fornitore: "I miei interventi",
  }[user.role];

  return (
    <div className="max-w-7xl">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
        <div className="flex items-center gap-2">
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">{title}</h1>
          <InfoHint section="dashboard" />
        </div>
        {user.role !== "fornitore" && (
          <Button data-testid="dashboard-new-ticket" onClick={() => navigate("/nuova")}
            className="h-14 px-6 text-lg font-bold bg-brand hover:bg-brand-dark text-white shadow-[0_4px_14px_0_rgba(122,25,61,0.35)] [text-shadow:0_1px_2px_rgba(0,0,0,0.35)]">
            <PlusCircle className="w-6 h-6 mr-2" /> Nuova Manutenzione
          </Button>
        )}
      </div>

      {/* SEGNALAZIONI */}
      <Card className={GROUP_CLS}>
        <SectionTitle icon={FolderOpen} label="Manutenzioni" />
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <StatCard testid="stat-aperte" icon={FolderOpen} label="Aperte" value={stats.aperte} tone="chiaro" onClick={() => toggle("aperte")} active={expanded === "aperte"} />
          <StatCard testid="stat-chiuse" icon={CheckCircle2} label="Chiuse" value={stats.chiuse} tone="scuro" onClick={() => toggle("chiuse")} active={expanded === "chiuse"} />
          <StatCard testid="stat-ritardo" icon={AlertTriangle} label="In ritardo" value={stats.in_ritardo} tone="alert" onClick={() => toggle("ritardo")} active={expanded === "ritardo"} />
          <StatCard testid="stat-valutazioni" icon={Star} label="Valut. media" value={stats.media_valutazioni ?? "—"} tone="neutro" onClick={() => toggle("valutazioni")} active={expanded === "valutazioni"} />
        </div>
      </Card>
      {["aperte", "chiuse", "ritardo", "valutazioni"].includes(expanded) && (
        <DetailPanel kind={expanded} tickets={tickets} sinistri={sinistri} preventivi={preventivi} onClose={() => setExpanded(null)} navigate={navigate} />
      )}

      {user.role === "admin" && (
        <>
          {/* Iter122ea · Popup "Configura mittente email" silenziato: resta configurabile da Impostazioni & Account → Email mittente */}
          {/* Iter123k245 · Nudging onboarding branding per i tenant trial (e paid senza logo) */}
          <BrandingSetupBanner navigate={navigate} user={user} />
          {/* Iter122co · Prompt post-import: banner nuovi condomìni da attivare */}
          <NuoviDaAttivareBanner navigate={navigate} />

          {/* PREVENTIVI */}
          <Card className={GROUP_CLS}>
            <SectionTitle icon={FileText} label="Preventivi" />
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <StatCard testid="stat-prev-aperti" icon={FileText} label="Aperti" value={stats.preventivi_aperti} tone="chiaro" onClick={() => toggle("prev_aperti")} active={expanded === "prev_aperti"} />
              <StatCard testid="stat-prev-chiusi" icon={CheckCircle2} label="Chiusi" value={stats.preventivi_chiusi} tone="scuro" onClick={() => toggle("prev_chiusi")} active={expanded === "prev_chiusi"} />
              <StatCard testid="stat-prev-ritardo" icon={AlertTriangle} label="Scaduti" value={stats.preventivi_scaduti} tone="alert" onClick={() => toggle("prev_ritardo")} active={expanded === "prev_ritardo"} />
              <StatCard testid="stat-prev-totale" icon={ClipboardList} label="Totali" value={stats.preventivi_totale} tone="neutro" />
            </div>
          </Card>
          {["prev_aperti", "prev_chiusi", "prev_ritardo"].includes(expanded) && (
            <DetailPanel kind={expanded} tickets={tickets} sinistri={sinistri} preventivi={preventivi} onClose={() => setExpanded(null)} navigate={navigate} />
          )}

          {/* SINISTRI · Iter122ey · solo se piano include feature "sinistri" (nascosto per Trial)
              Iter122k13 · Aggiunta card "Bozza" a fianco di Totali per evidenziare le pratiche non ancora denunciate */}
          {hasPlanFeature(user, "sinistri") && (
          <Card className={GROUP_CLS}>
            <SectionTitle icon={ShieldAlert} label="Sinistri" />
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <StatCard testid="stat-sin-aperti" icon={ShieldAlert} label="Aperti" value={stats.sinistri_aperti} tone="chiaro" onClick={() => toggle("sin_aperti")} active={expanded === "sin_aperti"} />
              <StatCard testid="stat-sin-definiti" icon={CheckCircle2} label="Definiti" value={stats.sinistri_definiti} tone="scuro" onClick={() => toggle("sin_definiti")} active={expanded === "sin_definiti"} />
              <StatCard testid="stat-sin-totale" icon={ClipboardList} label="Totali" value={stats.num_sinistri} tone="neutro" />
              <StatCard testid="stat-sin-bozza" icon={FileEdit} label="Bozza" value={stats.sinistri_bozza ?? 0} tone="alert" onClick={() => toggle("sin_bozza")} active={expanded === "sin_bozza"} />
            </div>
          </Card>
          )}
          {hasPlanFeature(user, "sinistri") && ["sin_aperti", "sin_definiti", "sin_bozza"].includes(expanded) && (
            <DetailPanel kind={expanded} tickets={tickets} sinistri={sinistri} preventivi={preventivi} onClose={() => setExpanded(null)} navigate={navigate} />
          )}

          {/* CREDIUS · Legale (Iter97) · Iter122ey · solo se piano include feature "credius" (Pro+) */}
          {hasPlanFeature(user, "credius") && <CrediusDashboardWidget navigate={navigate} />}

          {/* ATTIVITÀ PROGRAMMATE (RAS) · Iter122ey · solo se piano include feature "ras" (Full/Pro+) */}
          {hasPlanFeature(user, "ras") && (
          <Card className={GROUP_CLS}>
            <SectionTitle icon={Repeat} label="Attività programmate (RAS)" />
            <div className="grid grid-cols-3 gap-3 sm:gap-4">
              <StatCard testid="stat-ras-ok" icon={CheckCircle2} label="In regola" value={stats.attivita_ok ?? 0} tone="scuro" onClick={() => navigate("/ras")} />
              <StatCard testid="stat-ras-in-scadenza" icon={Repeat} label="In scadenza" value={stats.attivita_in_scadenza ?? 0} tone="chiaro" onClick={() => navigate("/ras")} />
              <StatCard testid="stat-ras-scadute" icon={AlertTriangle} label="Scadute" value={stats.attivita_scadute ?? 0} tone="alert" onClick={() => navigate("/ras")} />
            </div>
          </Card>
          )}

          {/* ANAGRAFICHE */}
          <Card className={GROUP_CLS}>
            <SectionTitle icon={Building2} label="Anagrafiche" />
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
              <StatCard testid="stat-ana-totale" icon={ClipboardList} label="Manutenzioni totali" value={stats.totale} tone="neutro" />
              <StatCard testid="stat-ana-condomini" icon={Building2} label="Condomìni" value={stats.num_condomini} tone="neutro" />
              <StatCard testid="stat-ana-fornitori" icon={Wrench} label="Fornitori" value={stats.num_fornitori} tone="neutro" />
            </div>
          </Card>
        </>
      )}

      {user.role === "fornitore" && (
        <Card data-testid="fornitore-whatsapp-card" className="p-5 border-2 border-green-200 bg-green-50/40 my-8 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="rounded-xl p-3 bg-green-100 text-green-700"><MessageCircle className="w-7 h-7" /></div>
            <div>
              <h3 className="font-display text-xl font-bold">Contatta lo Studio via WhatsApp</h3>
              <p className="text-base text-slate-600">Scrivi allo Ginius per richieste rapide e immediate sugli interventi.</p>
            </div>
          </div>
          <a href={`https://wa.me/393477440526?text=${encodeURIComponent("Ginius - ")}`} target="_blank" rel="noreferrer" data-testid="fornitore-whatsapp-link">
            <Button className="h-12 px-5 text-base font-bold bg-green-600 hover:bg-green-700">
              <MessageCircle className="w-5 h-5 mr-2" /> Apri WhatsApp
            </Button>
          </a>
        </Card>
      )}

      {/* Alert section */}
      {stats.alert_tickets.length > 0 && (
        <div className="mb-4 mt-8">
          <h2 className="font-display text-2xl font-bold mb-4 flex items-center gap-2 text-brand">
            <AlertTriangle className="w-7 h-7" /> Manutenzioni che richiedono attenzione
          </h2>
          <div className="space-y-3">
            {stats.alert_tickets.map((t) => (
              <div key={t.id} data-testid={`alert-ticket-${t.numero}`}
                onClick={() => navigate(`/segnalazioni/${t.id}`)}
                className={`cursor-pointer p-5 border-l-8 rounded-r-lg shadow-sm transition-colors flex flex-wrap items-center justify-between gap-3 ${
                  t.in_ritardo ? "border-brand bg-[#F8E8EE] hover:bg-[#F2D8E2]" : "border-[#D4AF5A] bg-[#FDF6F8] hover:bg-[#F8E8EE]"
                }`}>
                <div>
                  <div className="font-bold text-lg text-[#2B0712]">{t.numero} · {t.titolo}</div>
                  <div className="text-base text-brand-dark">{t.condominio_nome} — {t.alert_msg}</div>
                </div>
                <div className="flex items-center gap-2">
                  <PrioritaBadge priorita={t.priorita} />
                  <StatusBadge stato={t.stato} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const PANEL_META = {
  aperte: { title: "Manutenzioni aperte", icon: FolderOpen },
  chiuse: { title: "Manutenzioni chiuse", icon: CheckCircle2 },
  ritardo: { title: "Manutenzioni in ritardo", icon: AlertTriangle },
  valutazioni: { title: "Valutazioni raccolte", icon: Star },
  sin_aperti: { title: "Sinistri aperti", icon: ShieldAlert },
  sin_definiti: { title: "Sinistri definiti", icon: CheckCircle2 },
  sin_bozza: { title: "Sinistri in bozza (non ancora denunciati)", icon: FileEdit },
  prev_aperti: { title: "Preventivi aperti (in attesa o da valutare)", icon: FileText },
  prev_chiusi: { title: "Preventivi conclusi (accettati o rifiutati)", icon: CheckCircle2 },
  prev_ritardo: { title: "Preventivi scaduti senza risposta", icon: AlertTriangle },
};

function SectionTitle({ icon: Icon, label }) {
  return (
    <h2 className="font-display text-lg font-bold uppercase tracking-wide text-white flex items-center gap-2 mb-3 [text-shadow:0_1px_2px_rgba(0,0,0,0.45)]">
      <Icon className="w-5 h-5 text-[#D4AF5A]" /> {label}
    </h2>
  );
}

function DetailPanel({ kind, tickets, sinistri, preventivi, onClose, navigate }) {
  const meta = PANEL_META[kind];
  const Icon = meta.icon;
  const isSin = kind.startsWith("sin");
  const isPrev = kind.startsWith("prev");
  const loading = isSin ? sinistri === null : isPrev ? preventivi === null : tickets === null;
  const today = new Date().toISOString().slice(0, 10);

  let rows = [];
  if (!loading) {
    if (kind === "sin_aperti") {
      // Iter122k12 · Include tutte le fasi "Aperto-*" (Istruttoria/Valutazione/Liquidazione) + legacy "In gestione".
      // Le bozze restano fuori (fase "Bozza-Denuncia" / legacy "Aperto").
      rows = (sinistri || []).filter((s) => ["Aperto-Istruttoria", "Aperto-Valutazione", "Aperto-Liquidazione", "In gestione"].includes(s.fase));
    } else if (kind === "sin_definiti") {
      rows = (sinistri || []).filter((s) => ["Chiuso-Liquidato", "Definito"].includes(s.fase));
    } else if (kind === "sin_bozza") {
      // Iter122k13 · Solo bozze non ancora denunciate (fase "Bozza-Denuncia" + legacy "Aperto")
      rows = (sinistri || []).filter((s) => ["Bozza-Denuncia", "Aperto"].includes(s.fase));
    } else if (kind === "prev_aperti") {
      rows = (preventivi || []).filter((p) => ["Richiesto", "Inviato"].includes(p.stato));
    } else if (kind === "prev_chiusi") {
      rows = (preventivi || []).filter((p) => ["Accettato", "Rifiutato"].includes(p.stato));
    } else if (kind === "prev_ritardo") {
      rows = (preventivi || []).filter((p) => p.stato === "Richiesto" && p.data_scadenza && p.data_scadenza.slice(0, 10) < today);
    } else if (kind === "aperte") {
      rows = (tickets || []).filter((t) => OPEN_STATES.includes(t.stato));
    } else if (kind === "chiuse") {
      rows = (tickets || []).filter((t) => t.stato === "Chiusa");
    } else if (kind === "ritardo") {
      rows = (tickets || []).filter((t) => t.in_ritardo);
    } else if (kind === "valutazioni") {
      // Iter122k156 · Include ticket valutati dal condòmino, dallo Studio o entrambi
      rows = (tickets || []).filter((t) => t.feedback || t.feedback_studio);
    }
  }

  return (
    <Card className="p-5 border-2 mb-5" data-testid={`dashboard-panel-${kind}`}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl font-bold flex items-center gap-2"><Icon className="w-6 h-6 text-brand" /> {meta.title} ({loading ? "…" : rows.length})</h2>
        <Button variant="ghost" size="icon" data-testid="dashboard-panel-close" onClick={onClose}><X className="w-5 h-5" /></Button>
      </div>
      {loading ? (
        <div className="text-slate-500 py-6 text-center">Caricamento…</div>
      ) : rows.length === 0 ? (
        <div className="text-slate-500 py-6 text-center" data-testid={`dashboard-panel-empty-${kind}`}>Nessun elemento da mostrare.</div>
      ) : isSin ? (
        <div className="space-y-2">
          {rows.map((s) => {
            const definito = s.fase === "Definito";
            // Iter122k152 · Riporta anche sulla card anteprima dashboard i 3 chip
            // di stato pagamento/esito (coerenza con l'elenco Sinistri).
            const chipAttesa = s.esito_valutazione === "indennizzabile"
              && !s.pagamento_confermato && s.fase !== "Chiuso-Liquidato";
            const chipPagatoChiuso = s.esito_valutazione === "indennizzabile"
              && s.pagamento_confermato && s.fase === "Chiuso-Liquidato";
            const chipNonInd = s.esito_valutazione === "non_indennizzabile"
              && s.fase === "Chiuso-Liquidato";
            return (
              <div key={s.id} data-testid={`panel-sinistro-${s.numero_pratica}`} onClick={() => navigate("/sinistri")}
                className="cursor-pointer p-4 rounded-lg border-2 border-slate-200 hover:border-amber-400 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-bold text-slate-800">{s.descrizione || "Sinistro"}</div>
                  <div className="text-sm text-slate-500 flex items-center gap-1"><Building2 className="w-4 h-4" /> {s.condominio_nome} · {s.numero_pratica}</div>
                </div>
                <div className="shrink-0 flex flex-wrap items-center justify-end gap-1.5">
                  <span className={`px-3 py-1 rounded-full text-sm font-bold border-2 ${definito ? "bg-green-50 text-green-800 border-green-300" : "bg-amber-50 text-amber-800 border-amber-300"}`}>{s.fase}</span>
                  {chipAttesa && (
                    <span data-testid={`panel-sinistro-attesa-${s.numero_pratica}`}
                      className="px-2 py-0.5 rounded-full text-xs font-bold border uppercase tracking-wide bg-amber-100 text-amber-800 border-amber-400">
                      Attesa pagamento
                    </span>
                  )}
                  {chipPagatoChiuso && (
                    <span data-testid={`panel-sinistro-pagato-${s.numero_pratica}`}
                      className="px-2 py-0.5 rounded-full text-xs font-bold border uppercase tracking-wide bg-emerald-100 text-emerald-800 border-emerald-400">
                      Pagato · Chiuso
                    </span>
                  )}
                  {chipNonInd && (
                    <span data-testid={`panel-sinistro-nonind-${s.numero_pratica}`}
                      className="px-2 py-0.5 rounded-full text-xs font-bold border uppercase tracking-wide bg-red-100 text-red-800 border-red-400">
                      Non indennizzabile
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : isPrev ? (
        <div className="space-y-2">
          {rows.map((p) => {
            const scaduto = p.stato === "Richiesto" && p.data_scadenza && p.data_scadenza.slice(0, 10) < today;
            const badgeCls = scaduto ? "bg-red-100 text-red-800 border-red-300"
              : p.stato === "Accettato" ? "bg-green-50 text-green-800 border-green-300"
              : p.stato === "Rifiutato" ? "bg-slate-100 text-slate-600 border-slate-300"
              : p.stato === "Inviato" ? "bg-amber-50 text-amber-800 border-amber-300"
              : "bg-blue-50 text-blue-800 border-blue-300";
            const rowCls = scaduto ? "border-red-300 bg-red-50 hover:border-red-500" : "border-slate-200 hover:border-brand";
            return (
              <div key={p.id} data-testid={`panel-preventivo-${p.numero}`} onClick={() => navigate(`/preventivi/${p.id}`)}
                className={`cursor-pointer p-4 rounded-lg border-2 flex flex-wrap items-center justify-between gap-3 ${rowCls}`}>
                <div className="min-w-0">
                  <div className="font-bold text-slate-800">{p.numero} · {p.fornitore_nome}</div>
                  <div className="text-sm text-slate-500 flex items-center gap-1 truncate"><Building2 className="w-4 h-4 shrink-0" /> {p.condominio_nome || "—"} · {p.descrizione}</div>
                </div>
                <span className={`shrink-0 px-3 py-1 rounded-full text-sm font-bold border-2 ${badgeCls}`}>
                  {scaduto ? "Scaduto" : p.stato}{p.data_scadenza ? ` · ${p.data_scadenza.slice(0, 10)}` : ""}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((t) => (
            <div key={t.id} data-testid={`panel-ticket-${t.numero}`} onClick={() => navigate(`/segnalazioni/${t.id}`)}
              className="cursor-pointer p-4 rounded-lg border-2 border-slate-200 hover:border-brand flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-bold text-slate-800">{t.numero} · {t.titolo}</div>
                <div className="text-sm text-slate-500 flex items-center gap-1"><Building2 className="w-4 h-4" /> {t.condominio_nome}{t.fornitore_nome ? ` · ${t.fornitore_nome}` : ""}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {kind === "valutazioni" && (t.feedback || t.feedback_studio) ? (() => {
                  // Iter122k156 · Mostra le 2 valutazioni + media combinata sulla card panel
                  const mc = t.feedback?.media;
                  const ms = t.feedback_studio?.media;
                  const media = (mc != null && ms != null)
                    ? Math.round(((mc + ms) / 2) * 10) / 10
                    : (mc != null ? mc : ms);
                  return (
                    <div className="flex flex-col items-end gap-0.5 text-xs">
                      {mc != null && (
                        <div className="flex items-center gap-1" data-testid={`panel-valut-cond-${t.numero}`}>
                          <span className="text-slate-500">Cond.</span>
                          <StarRating value={Math.round(mc)} readOnly size={4} />
                          <span className="font-bold">{mc}</span>
                        </div>
                      )}
                      {ms != null && (
                        <div className="flex items-center gap-1" data-testid={`panel-valut-studio-${t.numero}`}>
                          <span className="text-slate-500">Studio</span>
                          <StarRating value={Math.round(ms)} readOnly size={4} />
                          <span className="font-bold">{ms}</span>
                        </div>
                      )}
                      {mc != null && ms != null && (
                        <div className="flex items-center gap-1 text-emerald-800"
                             data-testid={`panel-valut-media-${t.numero}`}>
                          <span className="font-semibold">Media</span>
                          <StarRating value={Math.round(media)} readOnly size={4} />
                          <span className="font-bold">{media}</span>
                        </div>
                      )}
                    </div>
                  );
                })() : (
                  <><PrioritaBadge priorita={t.priorita} /><StatusBadge stato={t.stato} /></>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/* ================ Iter97 · Widget Credius (3 pill contatore + Da affidare) ================ */
function CrediusDashboardWidget({ navigate }) {
  const [c, setC] = useState(null);
  const [cont, setCont] = useState(null);
  // Iter122ac · sezione "Contabilità in sospeso" occultabile, chiusa di default
  const [contOpen, setContOpen] = useState(false);
  useEffect(() => {
    api.get("/credius/dashboard-counters").then((r) => setC(r.data)).catch(() => setC({}));
    api.get("/credius/dashboard-contabile").then((r) => setCont(r.data)).catch(() => setCont(null));
  }, []);
  if (!c) return null;
  const tot = (c.stragiudiziale || 0) + (c.giudiziale || 0) + (c.sinistro || 0) + (c.da_affidare || 0);
  const fmtEur = (v) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(v || 0);
  const notulePend = cont?.notule_da_autorizzare?.count || 0;
  const feePend = (cont?.fee_pending_per_avvocato || []).reduce((a, f) => a + (f.count || 0), 0);
  const ricPend = cont?.ricorrenze_in_attesa_pagamento?.count || 0;
  const feePendTot = (cont?.fee_pending_per_avvocato || []).reduce((a, f) => a + (f.totale || 0), 0);
  const showContabile = notulePend > 0 || feePend > 0 || ricPend > 0;
  if (tot === 0 && !showContabile) return null;
  return (
    <Card className={GROUP_CLS} data-testid="credius-widget">
      <SectionTitle icon={Scale} label="Credius · Pratiche legali" />
      {tot > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          <StatCard testid="stat-cred-daaff" icon={AlertTriangle} label="Da affidare"
            value={c.da_affidare || 0} tone="alert" onClick={() => navigate("/credius")} />
          <StatCard testid="stat-cred-strag" icon={FileText} label="Stragiudiziali attive"
            value={c.stragiudiziale || 0} tone="chiaro" onClick={() => navigate("/credius")} />
          <StatCard testid="stat-cred-giud" icon={Gavel} label="Giudiziali attive"
            value={c.giudiziale || 0} tone="scuro" onClick={() => navigate("/credius")} />
          <StatCard testid="stat-cred-sin" icon={ShieldAlert} label="Sinistri attivi"
            value={c.sinistro || 0} tone="neutro" onClick={() => navigate("/credius")} />
        </div>
      )}
      {showContabile && (
        <div className="mt-3 pt-3 border-t border-slate-200">
          {/* Iter122ac · toggle apri/chiudi (chiuso di default) */}
          <button type="button" data-testid="cred-widget-contabile-toggle"
            onClick={() => setContOpen((v) => !v)}
            className="w-full flex items-center justify-between text-left group">
            <span className="text-xs font-bold uppercase text-slate-500 group-hover:text-brand transition-colors">
              Contabilità in sospeso <span className="text-slate-400">({notulePend + feePend + ricPend})</span>
            </span>
            <span className="text-slate-400 group-hover:text-brand transition-colors">
              {contOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </span>
          </button>
          {contOpen && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
            <button type="button" onClick={() => navigate("/credius")}
              data-testid="cred-widget-notule-pending"
              className="text-left bg-amber-50 border-2 border-amber-300 rounded-lg p-3 hover:bg-amber-100 transition">
              <div className="text-[11px] font-bold uppercase text-amber-800">Notule da autorizzare</div>
              <div className="flex items-baseline gap-2">
                <span className="text-xl font-bold text-amber-900">{notulePend}</span>
                <span className="text-sm text-amber-800">{fmtEur(cont.notule_da_autorizzare.totale)}</span>
              </div>
            </button>
            <button type="button" onClick={() => navigate("/credius")}
              data-testid="cred-widget-fee-pending"
              className="text-left bg-sky-50 border-2 border-sky-300 rounded-lg p-3 hover:bg-sky-100 transition">
              <div className="text-[11px] font-bold uppercase text-sky-800">Fee pending consolidabili</div>
              <div className="flex items-baseline gap-2">
                <span className="text-xl font-bold text-sky-900">{feePend}</span>
                <span className="text-sm text-sky-800">{fmtEur(feePendTot)}</span>
              </div>
            </button>
            <button type="button" onClick={() => navigate("/credius")}
              data-testid="cred-widget-ric-pending"
              className="text-left bg-emerald-50 border-2 border-emerald-300 rounded-lg p-3 hover:bg-emerald-100 transition">
              <div className="text-[11px] font-bold uppercase text-emerald-800">Ricorrenze in attesa</div>
              <div className="flex items-baseline gap-2">
                <span className="text-xl font-bold text-emerald-900">{ricPend}</span>
                <span className="text-sm text-emerald-800">{fmtEur(cont.ricorrenze_in_attesa_pagamento.totale)}</span>
              </div>
              {cont.ricorrenze_in_attesa_pagamento.eta_media_giorni > 0 && (
                <div className="text-[10px] text-emerald-700 mt-1">
                  Età media: {cont.ricorrenze_in_attesa_pagamento.eta_media_giorni} gg
                </div>
              )}
            </button>
          </div>
          )}
        </div>
      )}
    </Card>
  );
}

/** Iter123k245 · Banner onboarding branding per admin (prioritario per trial).
 *  Chiede di caricare logo/firma; il PDF resta privo di logo finché l'admin non li carica.
 *  Dismiss persistente (sessionStorage) a meno che il branding non sia completo. */
function BrandingSetupBanner({ navigate, user }) {
  const [assets, setAssets] = useState(null);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem("branding_setup_banner_dismissed") === "1");

  useEffect(() => {
    api.get("/me/tenant/branding-assets")
      .then((r) => setAssets(r.data))
      .catch(() => {});
  }, []);

  if (!assets) return null;
  const logoReady = !!assets.logo_url;
  const firmaReady = !!assets.firma_url;
  // Studio Izzo (tenant_id="studio") usa il logo Ginius di default → non mostriamo banner.
  if (user?.tenant_id === "studio") return null;
  // Branding già completo → niente banner.
  if (logoReady && firmaReady) return null;
  if (dismissed) return null;

  const dismiss = () => {
    sessionStorage.setItem("branding_setup_banner_dismissed", "1");
    setDismissed(true);
  };
  const isTrial = user?.account_type === "trial";
  const titolo = isTrial
    ? "Benvenuto! Personalizza i tuoi PDF in 1 minuto"
    : "Completa il branding dello Studio";
  const sub = isTrial
    ? "Carica logo e firma: appariranno subito su preventivi, sinistri e Credius. Finché non lo fai, i PDF escono senza logo."
    : (!logoReady && !firmaReady)
      ? "Logo e firma non sono ancora caricati — i PDF escono privi di intestazione."
      : (!logoReady ? "Manca il logo: il PDF esce senza intestazione grafica."
                    : "Manca la firma grafica.");

  return (
    <div className="mb-4 p-3 sm:p-4 border-2 border-brand rounded-xl bg-gradient-to-r from-[#FBEEF3] via-amber-50 to-white flex flex-wrap items-center gap-3"
         data-testid="banner-branding-setup">
      <Palette className="w-8 h-8 text-brand shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-slate-800">
          {titolo}
        </div>
        <div className="text-xs text-slate-600 leading-snug mt-0.5">
          {sub}
        </div>
      </div>
      <button type="button"
              className="h-10 px-4 rounded-lg bg-brand text-white font-semibold hover:bg-brand-dark"
              onClick={() => navigate("/branding-studio")}
              data-testid="banner-branding-cta">
        Configura ora
      </button>
      <button type="button" onClick={dismiss}
              className="p-1.5 rounded hover:bg-white/60 text-slate-500"
              data-testid="banner-branding-dismiss" title="Ricorda più tardi">
        <X className="w-5 h-5" />
      </button>
    </div>
  );
}


/** Iter122co · Banner Dashboard tenant · nuovi condomìni da attivare (post-import). */
function NuoviDaAttivareBanner({ navigate }) {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("nuovi_da_attivare_dismissed") === "1");

  useEffect(() => {
    api.get("/licenze-condominio/nuovi-da-attivare?days=30")
      .then((r) => setData(r.data))
      .catch(() => {});
  }, []);

  if (dismissed || !data || data.count === 0 || data.modalita_fatturazione !== "condominio") return null;

  const dismiss = () => { sessionStorage.setItem("nuovi_da_attivare_dismissed", "1"); setDismissed(true); };
  const nomi = (data.condomini || []).slice(0, 3).map((c) => c.nome).join(", ");
  const altri = data.count - Math.min(3, data.condomini?.length || 0);

  return (
    <div className="mb-4 p-3 sm:p-4 border-2 border-brand rounded-xl bg-gradient-to-r from-[#FBEEF3] to-white flex flex-wrap items-center gap-3"
         data-testid="banner-nuovi-condomini">
      <PlusCircle className="w-8 h-8 text-brand shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-slate-800">
          {data.count} {data.count === 1 ? "nuovo condominio" : "nuovi condomìni"} da attivare
        </div>
        <div className="text-xs text-slate-600 truncate">
          {nomi}{altri > 0 ? ` +${altri} altri` : ""} · importati negli ultimi {data.days} giorni senza licenza
        </div>
      </div>
      <button type="button" className="h-10 px-4 rounded-lg bg-brand text-white font-semibold hover:bg-brand-dark"
              onClick={() => navigate("/pannello#licenze-condomini")} data-testid="banner-attiva-licenze">
        Attiva licenze
      </button>
      <button type="button" onClick={dismiss} className="p-1.5 rounded hover:bg-white/60 text-slate-500" data-testid="banner-dismiss" title="Chiudi">
        <X className="w-5 h-5" />
      </button>
    </div>
  );
}

